<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const { t } = useI18n();
const route = useRoute();

const setupStages = computed(() => [
  { number: 1, title: t('INBOX_MGMT.CREATE_FLOW.CHANNEL.TITLE') },
  { number: 2, title: t('INBOX_MGMT.CREATE_FLOW.INBOX.TITLE') },
  { number: 3, title: t('INBOX_MGMT.CREATE_FLOW.AGENT.TITLE') },
  { number: 4, title: t('INBOX_MGMT.CREATE_FLOW.FINISH.TITLE') },
]);

const currentStage = computed(() => {
  const routeStages = {
    settings_inbox_new: 0,
    settings_inboxes_page_channel: 1,
    settings_inboxes_add_agents: 2,
    settings_inbox_finish: 3,
  };

  return routeStages[route.name] ?? 0;
});

// The final stage is the Finish route itself, so reaching it means the channel
// is already created and the builder is done.
const isComplete = computed(
  () => currentStage.value === setupStages.value.length - 1
);

const progressLabel = computed(() => {
  if (isComplete.value) {
    return t('INBOX_MGMT.CREATE_FLOW.BUILDER.COMPLETE');
  }

  return t('INBOX_MGMT.CREATE_FLOW.BUILDER.PROGRESS', {
    current: currentStage.value + 1,
    total: setupStages.value.length,
  });
});

const stageState = index => {
  if (index < currentStage.value) return 'complete';
  if (index === currentStage.value) return 'current';
  return 'upcoming';
};
</script>

<template>
  <main
    class="flex flex-col w-full h-full min-h-0"
    :aria-label="t('INBOX_MGMT.CREATE_FLOW.BUILDER.TITLE')"
  >
    <header class="border-b border-n-weak">
      <div
        class="flex flex-col w-full max-w-6xl gap-4 py-4 mx-auto sm:px-2 xl:flex-row xl:items-center xl:justify-between"
      >
        <div class="flex items-center gap-3 min-w-0 xl:shrink-0">
          <h1
            class="mb-0 text-base font-semibold tracking-tight truncate text-n-slate-12"
          >
            {{ t('INBOX_MGMT.CREATE_FLOW.BUILDER.TITLE') }}
          </h1>
          <span
            class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full shrink-0 bg-n-solid-2 text-n-slate-11 outline outline-1 outline-n-weak"
            aria-live="polite"
          >
            <Icon
              :icon="
                isComplete ? 'i-lucide-circle-check' : 'i-lucide-list-checks'
              "
              class="size-3.5"
              :class="isComplete ? 'text-n-teal-11' : 'text-n-violet-11'"
            />
            {{ progressLabel }}
          </span>
        </div>

        <ol
          class="flex flex-wrap items-center gap-x-1 gap-y-2 sm:gap-x-2"
          :aria-label="t('INBOX_MGMT.CREATE_FLOW.BUILDER.STAGES_LABEL')"
        >
          <li
            v-for="(stage, index) in setupStages"
            :key="stage.number"
            class="flex items-center gap-x-1 sm:gap-x-2"
            :aria-current="stageState(index) === 'current' ? 'step' : undefined"
          >
            <span
              class="flex items-center gap-2 px-2 py-1 rounded-full"
              :class="{
                'bg-n-violet-3': stageState(index) === 'current',
                'bg-n-teal-2': stageState(index) === 'complete',
              }"
            >
              <span
                class="flex items-center justify-center text-xs font-semibold rounded-full size-6 shrink-0"
                :class="{
                  'text-white bg-n-brand-violet':
                    stageState(index) === 'current',
                  'text-n-teal-11 bg-n-teal-3':
                    stageState(index) === 'complete',
                  'text-n-slate-10 bg-n-slate-3':
                    stageState(index) === 'upcoming',
                }"
                aria-hidden="true"
              >
                <Icon
                  v-if="stageState(index) === 'complete'"
                  icon="i-lucide-check"
                  class="size-3.5"
                />
                <template v-else>{{ stage.number }}</template>
              </span>
              <span
                class="text-xs font-medium sr-only sm:not-sr-only"
                :class="{
                  'text-n-slate-12': stageState(index) === 'current',
                  'text-n-teal-11': stageState(index) === 'complete',
                  'text-n-slate-10': stageState(index) === 'upcoming',
                }"
              >
                {{ stage.title }}
              </span>
            </span>
            <Icon
              v-if="index < setupStages.length - 1"
              icon="i-lucide-chevron-right"
              class="size-3.5 shrink-0 text-n-slate-8"
              aria-hidden="true"
            />
          </li>
        </ol>
      </div>
    </header>

    <div class="flex-1 min-h-0 overflow-y-auto">
      <router-view />
    </div>
  </main>
</template>
